import { faUserShield } from "@fortawesome/free-solid-svg-icons";
import React from "react";
import LegalPageLayout from "../Components/LegalPageLayout";

function PrivacySB() {
  return (
    <LegalPageLayout
      titleId="privacy-sb-title"
      icon={faUserShield}
      title="Privacy Policy (SmartBudget)"
      description={
        <p>
          Your privacy is our priority. This page explains how SmartBudget
          handles information.
        </p>
      }
    >
            <p className="effective-date">Effective Date: June 18, 2026</p>
            <p>
              SmartBudget is a budgeting app developed and operated by MileSoft
              to help users plan, track, and manage their money more clearly.
              MileSoft is the data controller for personal information processed
              through SmartBudget. For privacy requests, data protection officer
              (DPO) requests, and controller contact purposes, email us at{" "}
              <a href="mailto:privacy@milesoft.africa">
                privacy@milesoft.africa
              </a>
              .
            </p>
            <p>
              This Privacy Policy explains what information SmartBudget
              collects, how we use it, how we protect it, and what choices you
              have regarding your information.
            </p>
            <p>
              By using SmartBudget, you agree to the practices described in this
              Privacy Policy.
            </p>

            <h5>1. Information We Collect</h5>
            <p>
              SmartBudget collects only the information needed to provide
              account, budgeting, subscription, and support features.
            </p>

            <h6>1.1 Account Information</h6>
            <p>When you create an account or sign in, we may collect:</p>
            <ul>
              <li>Name</li>
              <li>Email address</li>
              <li>Login information</li>
              <li>User ID</li>
              <li>Account status</li>
              <li>App preferences, such as preferred currency and theme</li>
            </ul>

            <h6>1.2 Budgeting Information</h6>
            <p>
              When you use SmartBudget, you may enter financial planning
              information, including:
            </p>
            <ul>
              <li>Income sources</li>
              <li>Budget amounts</li>
              <li>Budget categories</li>
              <li>Planned spending</li>
              <li>Transactions</li>
              <li>Payees</li>
              <li>Transaction notes</li>
              <li>Spending summaries</li>
              <li>Preferred currency</li>
            </ul>
            <p>
              SmartBudget does not use your budgeting information to make
              credit, lending, insurance, or employment decisions.
            </p>

            <h6>1.3 Debt Tracking Information</h6>
            <p>
              If you use debt tracking features, you may enter information such
              as debt names, balances, interest rates, payment amounts, payment
              history, and loan planning details.
            </p>

            <h6>1.4 Subscription and Payment Information</h6>
            <p>
              If you subscribe to SmartBudget Premium, we may collect and store
              subscription-related information, including selected plan, payment
              amount, currency, transaction reference, subscription status, and
              subscription start and expiry dates.
            </p>
            <p>
              Payments are processed through a third-party payment provider.
              SmartBudget does not store your full card number, bank account
              number, PIN, CVV, or full payment credentials.
            </p>

            <h6>1.5 App Performance and Diagnostic Information</h6>
            <p>
              We may collect limited diagnostic information to help improve the
              app, such as crash logs, crash reports, error logs, app version,
              Device ID, device type, operating system version, app activity,
              anonymous usage analytics where enabled, and performance
              information.
            </p>

            <h5>2. How We Use Your Information and Legal Basis</h5>
            <p>We use your information to:</p>
            <ul>
              <li>Create and manage your account</li>
              <li>Allow you to sign in securely</li>
              <li>Save and display your budgets</li>
              <li>Track income, expenses, categories, and transactions</li>
              <li>Provide reports and summaries</li>
              <li>Support premium features</li>
              <li>Process and verify subscriptions</li>
              <li>Sync your information across devices</li>
              <li>Provide customer support</li>
              <li>Improve app performance and reliability</li>
              <li>Protect against misuse, fraud, or unauthorized access</li>
              <li>Comply with legal requirements where applicable</li>
            </ul>
            <p>
              Our legal basis for processing may include performance of a
              contract or requested service, consent, legitimate interests in
              operating and securing SmartBudget, legal obligations, and
              processing requested by you.
            </p>

            <h5>3. How Your Information Is Stored</h5>
            <p>
              SmartBudget uses trusted cloud service providers to store account,
              budgeting, subscription, and app data. Some information may also
              be stored temporarily on your device to allow the app to work
              better during poor or unavailable internet connectivity.
            </p>
            <p>
              We use reasonable technical and organizational safeguards,
              including HTTPS and encryption in transit where data is sent to
              our services or service providers, authentication controls, cloud
              security protections, and access controls. No system can guarantee
              absolute security.
            </p>

            <h5>4. Sharing of Information, Sales, and Targeted Advertising</h5>
            <p>
              MileSoft does not sell personal information. We do not share
              personal information with unrelated third parties for their own
              marketing.
            </p>
            <p>
              We may share limited information only when necessary to operate
              the app, including with service providers for authentication,
              cloud data storage, payment processing, subscription management,
              app diagnostics, customer support, app performance monitoring, and
              sign-in services.
            </p>
            <p>
              We may also disclose information when required to comply with the
              law, respond to lawful requests, protect the rights, safety, or
              security of users, MileSoft, or the public, prevent fraud or
              abuse, or during a business transfer.
            </p>
            <p>
              You may opt out of any sale, sharing, or targeted advertising use
              of personal information by emailing{" "}
              <a href="mailto:privacy@milesoft.africa">
                privacy@milesoft.africa
              </a>
              . We will honor applicable opt-out rights even though we do not
              sell personal information.
            </p>

            <h5>5. Refunds, Cancellations, and Subscription Changes</h5>
            <p>
              SmartBudget may offer paid subscription plans that unlock premium
              features. You may cancel your subscription at any time. Cancelling
              a subscription stops future renewals, but it does not
              automatically refund amounts already paid.
            </p>
            <p>
              Refund requests are reviewed based on the payment method used, the
              timing of the request, applicable platform rules, payment provider
              rules, and applicable law. If your subscription was purchased
              through Google Play, refund requests may need to be handled
              through Google Play according to Google Play refund policies.
            </p>
            <p>
              If your subscription was purchased through another supported
              payment provider, contact us at{" "}
              <a href="mailto:privacy@milesoft.africa">
                privacy@milesoft.africa
              </a>{" "}
              with your account email, transaction reference, payment date, and
              reason for the refund request.
            </p>
            <p>
              We may use payment and subscription information to verify
              subscription status, review refund requests, prevent duplicate
              claims or fraud, resolve payment disputes, and keep records
              required for legal, accounting, or business purposes.
            </p>

            <h5>6. Data Retention</h5>
            <p>
              We keep your information for as long as your account is active or
              as long as needed to provide SmartBudget services.
            </p>
            <p>
              We may keep limited information for a longer period where
              necessary for legal compliance, security, fraud prevention,
              dispute resolution, subscription management, refund processing,
              backups, or accounting and business records required by law.
            </p>
            <p>
              When your account is deleted, we will delete or anonymize your
              personal information unless we are required or permitted to retain
              limited records for legitimate reasons.
            </p>

            <h5>7. Account Deletion</h5>
            <p>You may request deletion of your SmartBudget account.</p>
            <p>
              Where available, you can delete your account inside the app
              through:
            </p>
            <code> Account &gt; Delete Account</code>
            <p>
              You may also request deletion by contacting us at{" "}
              <a href="mailto:privacy@milesoft.africa">
                privacy@milesoft.africa
              </a>
              . Please use the email address linked to your SmartBudget account
              so we can identify your account.
            </p>

            <h5>8. Your Choices and Privacy Rights</h5>
            <p>
              Depending on where you live, including under the GDPR, CCPA/CPRA,
              VCDPA, LGPD, and other applicable privacy laws, you may have the
              right to request access to personal information we collect, request
              correction or rectification of inaccurate information, request
              deletion, request a copy or portable export, object to or restrict
              certain processing, withdraw consent where processing is based on
              consent, and know whether personal information is sold or shared.
            </p>
            <p>
              California residents may exercise CCPA/CPRA rights to know,
              access, correct, delete, and opt out of sale or sharing. Virginia
              residents may exercise VCDPA rights to access, correct, delete,
              obtain a copy, and opt out of targeted advertising, sale, or
              certain profiling. Users in Brazil may exercise LGPD rights,
              including confirmation, access, correction, anonymization,
              deletion, portability, information about sharing, and consent
              withdrawal. Users in the EEA or UK may exercise GDPR rights,
              including access, rectification, erasure, restriction, objection,
              portability, and the right to lodge a complaint with a supervisory
              authority.
            </p>
            <p>
              To make a request, contact{" "}
              <a href="mailto:privacy@milesoft.africa">
                privacy@milesoft.africa
              </a>
              .
            </p>

            <h5>9. Children's Privacy</h5>
            <p>SmartBudget is not intended for children under the age of 13.</p>
            <p>
              We do not knowingly collect personal information from children
              under 13. If we learn that a child has provided personal
              information without appropriate consent, we will take reasonable
              steps to delete that information.
            </p>

            <h5>10. International Data Processing</h5>
            <p>
              SmartBudget may use cloud-based service providers. Your
              information may be processed or stored in countries other than
              your own, where data protection laws may differ from those in your
              country.
            </p>

            <h5>11. Data Safety Summary</h5>
            <p>
              SmartBudget may collect or process the following types of data:
            </p>
            <table>
              <thead>
                <tr>
                  <th>Data Type</th>
                  <th>Purpose</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td data-label="Data Type">Name</td>
                  <td data-label="Purpose">Account profile</td>
                </tr>
                <tr>
                  <td data-label="Data Type">Email address</td>
                  <td data-label="Purpose">
                    Login, account support, communication
                  </td>
                </tr>
                <tr>
                  <td data-label="Data Type">User ID</td>
                  <td data-label="Purpose">Account identification</td>
                </tr>
                <tr>
                  <td data-label="Data Type">Financial planning information</td>
                  <td data-label="Purpose">
                    Budgeting, reports, debt tracking
                  </td>
                </tr>
                <tr>
                  <td data-label="Data Type">
                    Purchase or subscription information
                  </td>
                  <td data-label="Purpose">
                    Premium access and subscription management
                  </td>
                </tr>
                <tr>
                  <td data-label="Data Type">App activity</td>
                  <td data-label="Purpose">
                    App functionality and improvement
                  </td>
                </tr>
                <tr>
                  <td data-label="Data Type">Crash and diagnostic data</td>
                  <td data-label="Purpose">Reliability and bug fixing</td>
                </tr>
                <tr>
                  <td data-label="Data Type">
                    Device ID, device, or app information
                  </td>
                  <td data-label="Purpose">
                    Security, diagnostics, and app performance
                  </td>
                </tr>
              </tbody>
            </table>
            <p>SmartBudget does not sell user data.</p>

            <h5>12. Changes to This Privacy Policy</h5>
            <p>
              MileSoft may update this Privacy Policy from time to time. When we
              update it, we will revise the effective date and may notify users
              through the app, email, or another reasonable method.
            </p>

            <h5>13. Contact Us</h5>
            <p>
              <strong>Company and App Provider:</strong> MileSoft
              <br />
              <strong>App:</strong> SmartBudget
              <br />
              <strong>Controller and DPO Contact:</strong>{" "}
              <a href="mailto:privacy@milesoft.africa">
                privacy@milesoft.africa
              </a>
              <br />
              <strong>Location:</strong> Malawi
            </p>
    </LegalPageLayout>
  );
}

export default PrivacySB;
